// src/store/session.ts
import { create } from "zustand";
import type { User } from "../api/types";
import type { RegisterResponse } from "../api/auth";
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  requestMagicLink as apiRequestMagicLink,
} from "../api/auth";
import { getMe } from "../api/endpoints";
import { getStoredToken } from "../api/http";

type SessionState = {
  user: User | null;
  loading: boolean;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<RegisterResponse>;
  requestMagicLink: (email: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const useSession = create<SessionState>((set) => ({
  user: null,
  loading: true,

  bootstrap: async () => {
    try {
      const token = getStoredToken();
      if (!token) {
        set({ user: null, loading: false });
        return;
      }
      const me = await getMe();
      set({ user: me, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },

  login: async (email, password) => {
    const user = await apiLogin(email, password);
    set({ user });
  },

  register: async (email, password, displayName) => {
    // Register does NOT log the user in anymore
    return await apiRegister(email, password, displayName);
  },

  requestMagicLink: async (email) => {
    await apiRequestMagicLink(email);
  },

  logout: async () => {
    await apiLogout();
    set({ user: null });
  },
}));
